import { Checkbox } from "@mui/material";
import { ArrowRight, ChevronLeft, ChevronRight, X } from "lucide-react";
import * as Select from "@radix-ui/react-select";
import {
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import { Button } from "@components/ui/button";
import { useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@redux/store";
import {
  updateTriggerEvents,
  updatePublicationPeriod,
  updateEventDate,
  updateSearchCriteria,
  saveFiltersState,
} from "@redux/slices/filter";
import SimpleBar from "simplebar-react";
import { useState } from "react";
import { Textarea } from "@components/ui/textarea";
import { FilterAtom } from "@/App";
import {useRecoilState } from "recoil";

interface Props {
  handleClose: () => void;
}

const TriggerEventsData = {
  "Construction Projects": [
    "Construction Plans",
    "New buildings and expansions",
    "Start of construction phase",
  ],
  Events: ["Awards", "Lottery game", "Trade fair attendance"],
  Expansion: ["Expansion abroad", "Merger & acquisitions"],
  "Financial Decline": [
    "Cost cutting measures",
    "Financial losses",
    "Reduction in earnings",
    "Reduction in revenue",
  ],
  "Financial growth": [
    "Financing / Equity increase",
    "Increase in earnings",
    "Increase in revenue",
  ],
  "Job offers": [
    "Job offers on job portals",
    "Job offers on social media",
    "Job offers on website",
  ],
  "Negative Events": [
    "Bankruptcy notice",
    "Lawsuit",
    "Product recall",
    "Site closure",
    "Strike",
  ],
  "Partnership & Coorperation": [
    "Change of management",
    "Staff cuts",
    "Team expansion",
  ],
  "Positive Events": [
    "New major contract/customer",
    "Number of customers",
    "Partnership & cooperation",
  ],
  "Register Trigger Events": [
    "Annual financial statement",
    "Change in capital",
    "Liquidation",
    "New address/location",
    "New registration",
  ],
};

const TriggerEventsDetail: React.FC<Props> = ({ handleClose }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { events, publicationPeriod, eventDate, searchCriteria } = useSelector(
    (state: RootState) => state.filter.newsAndTriggerEvents
  );

  const [dropdowns, setDropdowns] = useState<{ [key: string]: boolean }>({});

  const toggleDropdown = (category: TriggerEventCategory) => {
    setDropdowns((prevState) => ({
      ...prevState,
      [category]: !prevState[category],
    }));
  };

  type TriggerEventCategory = keyof typeof TriggerEventsData;

  const handleTriggerEventChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    parentCategory?: TriggerEventCategory
  ) => {
    const value = event.target.id;
    const isChecked = event.target.checked;

    let updatedTriggerEvents;

    if (parentCategory) {
      const subfields = TriggerEventsData[parentCategory];
      if (!subfields) return;
      updatedTriggerEvents = isChecked
        ? [...events, ...subfields]
        : events.filter((item: any) => !subfields.includes(item));
    } else {
      updatedTriggerEvents = isChecked
        ? [...events, value]
        : events.filter((item: any) => item !== value);
    }

    dispatch(updateTriggerEvents(updatedTriggerEvents));
  };

  const handlePublicationPeriodChange = (value: string) => {
    dispatch(updatePublicationPeriod(value));
  };

  const handleEventDateChange = (value: string) => {
    dispatch(updateEventDate(value));
  };

  const handleSearchCriteriaChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    dispatch(updateSearchCriteria(event.target.value));
  };

  const [filter, setFilter] = useRecoilState(FilterAtom);
  const handleClick = (id: string) => {
    console.log("now clicked is " + id);
    // setClickedIds((prevStrings) => [...prevStrings, id]);
    setFilter((prevFilters: string[]) => [...prevFilters, id]); // Append to the atom
    console.log("I am here now");
  };

  return (
    <div className="flex flex-col justify-between h-full p-8">
      <div>
        <div className="flex justify-between items-center mb-5">
          <h4 className="text-xl font-bold flex items-center -ms-2">
            <ChevronLeft
              size={22}
              className="me-3 cursor-pointer"
              onClick={() => navigate(-1)}
            />
            Trigger Event
          </h4>
          <X onClick={handleClose} className="cursor-pointer" />
        </div>
        <div className="flex gap-4">
          <div className="bg-white rounded-lg w-2/3 px-6 py-4">
            <h4 className="text-lg font-bold flex items-center mb-4">
              Select Trigger Event
            </h4>
            <SimpleBar style={{ maxHeight: 310 }}>
              {Object.keys(TriggerEventsData).map((parentCategory) => (
                <div key={parentCategory}>
                  <div
                    className="flex items-center text-md justify-between font-semibold cursor-pointer pt-3 pb-1 border-b border-[#DBDBDB]"
                    onClick={() =>
                      toggleDropdown(parentCategory as TriggerEventCategory)
                    }
                  >
                    <div className="flex items-center">
                      <ChevronRight
                        size={22}
                        className={
                          dropdowns[parentCategory] ? "rotate-90" : ""
                        }
                      />
                      <h6 className="ms-2">{parentCategory}</h6>
                    </div>
                    <Checkbox
                      id={parentCategory}
                      checked={TriggerEventsData[parentCategory as TriggerEventCategory].every(
                        (subfield) => events.includes(subfield)
                      )}
                      onChange={(e) =>
                        handleTriggerEventChange(
                          e,
                          parentCategory as TriggerEventCategory
                        )
                      }
                      sx={{
                        color: '#1CB5AF', // Unchecked color
                        '&.Mui-checked': {
                          color: '#1CB5AF', // Checked color
                        },
                        padding:0
                      }}
                    />
                  </div>

                  {dropdowns[parentCategory] && (
                    <div>
                      {TriggerEventsData[parentCategory as TriggerEventCategory].map(
                        (subfield) => (
                          <div
                            key={subfield}
                            className="flex items-center text-sm pt-3 pb-1 pl-6 border-b border-[#DBDBDB]"
                          >
                            <Checkbox
                              id={subfield}
                              checked={events.includes(subfield)}
                              onChange={(e) => handleTriggerEventChange(e)}
                              sx={{ padding: 0 }}
                            />
                            <h6 className="ms-2">{subfield}</h6>
                          </div>
                        )
                      )}
                    </div>
                  )}
                </div>
              ))}
            </SimpleBar>
          </div>
          <div className="bg-white rounded-lg w-1/3 px-6 py-4">
            <h4 className="text-lg font-bold flex items-center mb-4">
              Select Trigger Event
            </h4>
            <p className="text-[#71717A] text-sm mb-1">Publication Period</p>
            <Select.Root
              value={publicationPeriod}
              onValueChange={handlePublicationPeriodChange}
            >
              <SelectTrigger className="SelectTrigger bg-white">
                <SelectValue placeholder="Select Publication Period" />
              </SelectTrigger>
              <SelectContent className="SelectContent">
                <SelectItem value="custom">custom fixed period</SelectItem>
                <SelectItem value="all">all results</SelectItem>
                <SelectItem value="30days">last 30 days</SelectItem>
                <SelectItem value="lastYear">last year</SelectItem>
                <SelectItem value="3months">last 3 months</SelectItem>
                <SelectItem value="6months">last 6 months</SelectItem>
                <SelectItem value="7days">last 7 days</SelectItem>
              </SelectContent>
            </Select.Root>
            <p className="text-[#71717A] text-sm mb-1 pt-3">Event Date</p>
            <Select.Root
              value={eventDate}
              onValueChange={handleEventDateChange}
            >
              <SelectTrigger className="SelectTrigger bg-white">
                <SelectValue placeholder="Select Event Date" />
              </SelectTrigger>
              <SelectContent className="SelectContent">
                <SelectItem value="future">
                  future ({"> "}30days from today)
                </SelectItem>
                <SelectItem value="past">
                  past ({"> "}30days before today)
                </SelectItem>
                <SelectItem value="current">
                  current (today += 30 days)
                </SelectItem>
              </SelectContent>
            </Select.Root>
            <p className="text-sm mb-2 pt-6 capitalize font-medium">
              Browse Selected Trigger Events
            </p>
            <p className="text-[#71717A] text-sm mb-1">
              Define search criteriasasdasd
            </p>
            <Textarea
              value={searchCriteria}
              onChange={handleSearchCriteriaChange}
              placeholder="Add a keyword"
              className="px-3 py-3 rounded-lg border border-[#DBDBDB] w-full "
            />
          </div>
        </div>
      </div>
      <div className="flex justify-end">
        <Button 
        onClick={() => {
          // handleClick("Trigger Events"); // Replace someValue with the actual value you want to pass
          // Navigate back
          navigate(-1)
        }} 
        className="bg-[#1CB5AF]"
        >Save</Button>
      </div>
    </div>
  );
};

export default TriggerEventsDetail;
